/**
 * DumpGpsCoverageBar
 *
 * GPS coverage visualization — time-based bar showing GPS data density.
 * Visual style mirrors xconvert-webapp GpsVehicleBar.tsx.
 *
 * No API calls — all data comes from the local dump.
 */
import * as React from 'react'
import { useState, useMemo } from 'react'
import { Icon, Popup, Segment } from 'semantic-ui-react'
import {
    DumpPosition,
    GpsCoverageChunk,
    buildGpsCoverageChunks,
    pickChunkMinutes,
    formatTs,
    formatTsWithTz,
} from './dumpVisualizerUtils'

// ─── Types ────────────────────────────────────────────────────────────────────

interface DumpGpsCoverageBarProps {
    positions: DumpPosition[]
    from: Date
    to: Date
    /** If provided, overrides the auto-selected chunk size */
    chunkMinutes?: number
    /** Called when user picks/clears a position in the selected chunk */
    onPositionSelect?: (pos: DumpPosition | null) => void
    /** Called when user selects a block — passes all positions in that block */
    onChunkSelect?: (positions: DumpPosition[], chunk: GpsCoverageChunk | null) => void
    /** Index of the externally-selected chunk (for highlighting from parent) */
    selectedChunkIdx?: number | null
    /** Externally-selected position (used to sync map marker click <-> table row highlight) */
    selectedPosition?: DumpPosition | null
}

// ─── Colors ───────────────────────────────────────────────────────────────────

const COLOR_OK = '#21ba45'
const COLOR_MISSING = '#db2828'
const COLOR_SELECTED_BORDER = '#2185d0'
const MIN_BLOCK_WIDTH = 4 // px — ensures red gaps are always visible

// ─── Component ────────────────────────────────────────────────────────────────

export const DumpGpsCoverageBar: React.FC<DumpGpsCoverageBarProps> = ({
    positions,
    from,
    to,
    chunkMinutes: chunkMinutesProp,
    onPositionSelect,
    onChunkSelect,
    selectedChunkIdx: externalSelectedIdx,
    selectedPosition: externalSelectedPosition,
}) => {
    const [internalSelectedIdx, setInternalSelectedIdx] = useState<number | null>(null)
    const [internalSelectedPosition, setInternalSelectedPosition] = useState<DumpPosition | undefined>(undefined)

    const selectedChunkIdx = externalSelectedIdx !== undefined ? externalSelectedIdx : internalSelectedIdx
    const selectedPosition = externalSelectedPosition !== undefined
        ? (externalSelectedPosition ?? undefined)
        : internalSelectedPosition

    const autoChunkMinutes = useMemo(
        () => pickChunkMinutes(from, to, 100),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [from.getTime(), to.getTime()]
    )

    const chunkMinutes = chunkMinutesProp ?? autoChunkMinutes

    const chunks: GpsCoverageChunk[] = useMemo(
        () => buildGpsCoverageChunks(positions, from, to, chunkMinutes),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [positions, from.getTime(), to.getTime(), chunkMinutes]
    )

    const totalChunks = chunks.length
    const okChunks = chunks.filter(c => c.count > 0).length
    const missingChunks = totalChunks - okChunks
    const coveragePct = totalChunks > 0 ? ((okChunks / totalChunks) * 100).toFixed(1) : '—'

    // Max count across all chunks — used for proportional flex width (like GpsVehicleBar)
    const maxCount = Math.max(...chunks.map(c => c.count), 1)

    function handleChunkClick(idx: number) {
        const isDeselect = internalSelectedIdx === idx
        const newIdx = isDeselect ? null : idx
        setInternalSelectedIdx(newIdx)
        setInternalSelectedPosition(undefined)
        onPositionSelect?.(null)
        if (onChunkSelect) {
            const chunk = newIdx != null ? chunks[newIdx] : null
            onChunkSelect(chunk?.positions ?? [], chunk ?? null)
        }
    }

    function renderBar() {
        if (chunks.length === 0) {
            return <div style={{ color: '#888', padding: '4px 0', fontSize: '0.85em' }}>No time chunks to display.</div>
        }
        if (to.getTime() - from.getTime() <= 0) {
            return <div style={{ color: '#888', padding: '4px 0', fontSize: '0.85em' }}>Invalid time range.</div>
        }

        return (
            <div
                style={{
                    display: 'flex',
                    gap: 1,
                    height: 30,
                    alignItems: 'stretch',
                    borderRadius: 3,
                    border: '1px solid rgba(34,36,38,.15)',
                    overflow: 'auto',
                    userSelect: 'none',
                    width: '100%',
                }}
            >
                {chunks.map((chunk, idx) => {
                    const isSelected = selectedChunkIdx === idx
                    const color = chunk.count > 0 ? COLOR_OK : COLOR_MISSING
                    // Proportional flex like GpsVehicleBar — missing chunks still show at minWidth
                    const flex = chunk.count === 0 ? 0 : chunk.count / maxCount

                    const popupContent = (
                        <div style={{ width: 240 }}>
                            {formatTs(chunk.start)} – {formatTs(chunk.end)}
                            <br />
                            Positions: <strong>{chunk.count}</strong>
                        </div>
                    )

                    return (
                        <Popup
                            key={idx}
                            content={popupContent}
                            mouseEnterDelay={100}
                            mouseLeaveDelay={0}
                            trigger={
                                <div
                                    style={{
                                        flex,
                                        minWidth: MIN_BLOCK_WIDTH,
                                        background: color,
                                        border: isSelected ? `2px solid ${COLOR_SELECTED_BORDER}` : 'none',
                                        cursor: 'pointer',
                                        transition: 'flex 0.2s',
                                        boxSizing: 'border-box',
                                    }}
                                    onMouseEnter={e => {
                                        const el = e.currentTarget as HTMLDivElement
                                        el.style.boxShadow = '0 4px 16px rgba(0,0,0,0.18)'
                                        if (!isSelected) el.style.border = '1px solid rgba(0,0,0,0.4)'
                                    }}
                                    onMouseLeave={e => {
                                        const el = e.currentTarget as HTMLDivElement
                                        el.style.boxShadow = ''
                                        el.style.border = isSelected ? `2px solid ${COLOR_SELECTED_BORDER}` : 'none'
                                    }}
                                    onClick={() => handleChunkClick(idx)}
                                />
                            }
                        />
                    )
                })}
            </div>
        )
    }

    function renderSummary() {
        return (
            <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
                <span>Total chunks: <strong>{totalChunks}</strong></span>
                {' | '}
                <span style={{ color: COLOR_MISSING }}>Missing: <strong>{missingChunks}</strong></span>
                {' | '}
                <span style={{ color: COLOR_OK }}>OK: <strong>{okChunks}</strong></span>
                {' | '}
                <span style={{ color: COLOR_OK }}>Coverage: <strong>{coveragePct}%</strong></span>
                {selectedChunkIdx != null && (
                    <>
                        {' | '}
                        <span
                            style={{ color: '#888', cursor: 'pointer' }}
                            onClick={() => {
                                setInternalSelectedIdx(null)
                                setInternalSelectedPosition(undefined)
                                onPositionSelect?.(null)
                                onChunkSelect?.([], null)
                            }}
                        >
                            <Icon name="remove" />clear selection
                        </span>
                    </>
                )}
            </div>
        )
    }

    function renderSelectedBlocksInfo() {
        const selectedIdx = externalSelectedIdx !== undefined ? externalSelectedIdx : internalSelectedIdx
        if (selectedIdx == null) return null
        const chunk = chunks[selectedIdx]
        if (!chunk) return null

        return (
            <Segment style={{ marginTop: 5, padding: '5px 10px', overflowX: 'auto', overflowY: 'auto', maxHeight: 300 }}>
                {chunk.positions.length === 0 ? (
                    <div style={{ opacity: 0.7, marginTop: 5 }}>No positions in this block</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 5, fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#f9fafb', borderBottom: '1px solid rgba(34,36,38,.15)' }}>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>Actions</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>_id</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>accuracy</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>externalMessageTimestamp</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>gpsFix</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>heading</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>latitude</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>longitude</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>odometer</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>source</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>speed</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>timestamp</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>totalFuel</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>virtualOdometer</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>virtualSpeed</th>
                                <th style={{ padding: '4px 6px', textAlign: 'left' }}>weight</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chunk.positions.map((p, idx) => {
                                const isSelectedPos = p._id
                                    ? p._id === selectedPosition?._id
                                    : (p.latitude === selectedPosition?.latitude && p.longitude === selectedPosition?.longitude)
                                return (
                                    <tr
                                        key={idx}
                                        style={{
                                            background: isSelectedPos ? '#f1f8e9' : undefined,
                                            borderBottom: '1px solid rgba(34,36,38,.1)',
                                        }}
                                    >
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>
                                            <Icon
                                                circular
                                                size="small"
                                                inverted
                                                color="teal"
                                                name="map marker alternate"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setInternalSelectedPosition(p)
                                                    onPositionSelect?.(p)
                                                }}
                                            />
                                        </td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p._id ?? ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.accuracy ?? ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.externalMessageTimestamp ? formatTsWithTz(p.externalMessageTimestamp) : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.gpsFix != null ? (p.gpsFix ? '✅' : '❌') : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.heading != null ? p.heading.toFixed(0) + '°' : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.latitude?.toFixed(6) ?? ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.longitude?.toFixed(6) ?? ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{typeof p.odometer === 'number' ? p.odometer.toFixed(1) : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.source ?? ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{typeof p.speed === 'number' ? p.speed.toFixed(1) + ' km/h' : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{formatTsWithTz(p.timestamp)}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{p.totalFuel ?? ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{typeof p.virtualOdometer === 'number' ? p.virtualOdometer.toFixed(1) : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{typeof p.virtualSpeed === 'number' ? p.virtualSpeed.toFixed(1) : ''}</td>
                                        <td style={{ padding: '4px 6px', whiteSpace: 'nowrap' }}>{typeof p.weight === 'number' ? p.weight : ''}</td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </Segment>
        )
    }

    return (
        <div>
            <div style={{ fontSize: '0.82em', color: '#777', marginBottom: 4 }}>
                Time-based (chunk: {chunkMinutes} min)
            </div>
            {renderBar()}
            {renderSummary()}
            {renderSelectedBlocksInfo()}
        </div>
    )
}
